import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { GoogleLogo, MicrosoftOutlookLogo, Plug, PlugsConnected, Info, CheckCircle, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return s; }
}

export default function IntegrationsTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({ google: false, microsoft: false });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/integrations/calendar/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      toast.error("Errore caricamento stato integrazioni");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Toast on OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected")) toast.success("Google Calendar connesso");
    if (params.get("ms_connected")) toast.success("Microsoft Outlook connesso");
    if (params.get("google_error")) toast.error(`Errore Google: ${params.get("google_error")}`);
    if (params.get("ms_error")) toast.error(`Errore Microsoft: ${params.get("ms_error")}`);
  }, []);

  const connect = async (provider) => {
    setBusy(p => ({ ...p, [provider]: true }));
    try {
      const path = provider === "google" ? "google-calendar" : "microsoft-calendar";
      const r = await axios.get(`${API}/integrations/${path}/authorize`, { withCredentials: true });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Provider non configurato");
      setBusy(p => ({ ...p, [provider]: false }));
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnettere ${provider === "google" ? "Google Calendar" : "Microsoft Outlook"}? I colloqui esistenti restano sul calendario.`)) return;
    setBusy(p => ({ ...p, [provider]: true }));
    try {
      await axios.delete(`${API}/integrations/calendar/${provider}`, { withCredentials: true });
      toast.success("Disconnesso");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(p => ({ ...p, [provider]: false }));
    }
  };

  const toggleSync = async (provider, enabled) => {
    try {
      await axios.post(`${API}/integrations/calendar/${provider}/sync-toggle`, { enabled }, { withCredentials: true });
      toast.success(`Sync ${enabled ? "abilitato" : "disabilitato"}`);
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  if (loading) return <Card className="border-[#0D1F3C]/8 max-w-2xl"><CardContent className="p-6 text-sm text-[#0D1F3C]/60">Caricamento…</CardContent></Card>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="integrations-tab">
      <Card className="border-[#0D1F3C]/8 bg-blue-50/30">
        <CardContent className="p-4 flex gap-3 items-start">
          <Info size={20} weight="duotone" className="text-[#1B4FD8] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-[#0D1F3C] font-medium">Sincronizzazione bidirezionale</p>
            <p className="text-xs text-[#0D1F3C]/70 mt-1">
              Selly crea un calendario dedicato <strong>"Selly Colloqui"</strong> e sincronizza solo i colloqui creati in Selly.
              Eventi creati direttamente sul tuo calendario <strong>non</strong> diventano colloqui Selly.
              Modifiche di data/ora sul calendario esterno aggiornano il colloquio in Selly.
            </p>
          </div>
        </CardContent>
      </Card>

      <ProviderCard
        provider="google"
        label="Google Calendar"
        Icon={GoogleLogo}
        color="#EA4335"
        info={status?.google}
        busy={busy.google}
        onConnect={() => connect("google")}
        onDisconnect={() => disconnect("google")}
        onToggle={(v) => toggleSync("google", v)}
      />

      <ProviderCard
        provider="microsoft"
        label="Microsoft Outlook Calendar"
        Icon={MicrosoftOutlookLogo}
        color="#0078D4"
        info={status?.microsoft}
        busy={busy.microsoft}
        onConnect={() => connect("microsoft")}
        onDisconnect={() => disconnect("microsoft")}
        onToggle={(v) => toggleSync("microsoft", v)}
      />
    </div>
  );
}

function ProviderCard({ provider, label, Icon, color, info, busy, onConnect, onDisconnect, onToggle }) {
  const connected = info?.connected;
  const configured = info?.configured_globally;
  return (
    <Card className="border-[#0D1F3C]/8" data-testid={`provider-card-${provider}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2.5">
            <Icon size={22} weight="fill" style={{ color }} />
            {label}
          </CardTitle>
          {connected ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1.5">
              <CheckCircle size={12} weight="fill" />
              Connesso
            </Badge>
          ) : configured ? (
            <Badge className="bg-gray-100 text-gray-700 border-gray-200">Non connesso</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1.5">
              <Warning size={12} weight="fill" />
              Non configurato (admin)
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {connected && (
          <>
            <div className="text-xs text-[#0D1F3C]/70 space-y-1">
              <div><span className="opacity-60">Account:</span> <strong>{info.account_email || "—"}</strong></div>
              <div><span className="opacity-60">Connesso il:</span> {formatDate(info.connected_at)}</div>
              {info.calendar_id && <div><span className="opacity-60">Calendario:</span> Selly Colloqui</div>}
              {info.last_error && (
                <div className="text-red-600 mt-2 flex gap-1.5 items-start">
                  <Warning size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <span className="text-xs">Errore: {info.last_error}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#0D1F3C]/8">
              <div>
                <p className="text-sm font-medium text-[#0D1F3C]">Sincronizza colloqui</p>
                <p className="text-xs text-[#0D1F3C]/60 mt-0.5">Push e pull automatici per i colloqui di questo workspace</p>
              </div>
              <Switch
                checked={info.sync_enabled !== false}
                onCheckedChange={onToggle}
                data-testid={`sync-toggle-${provider}`}
              />
            </div>
          </>
        )}
        <div className="flex gap-2 pt-1">
          {!connected && (
            <Button
              onClick={onConnect}
              disabled={busy || !configured}
              className="bg-[#1B4FD8] hover:bg-[#0D1F3C] gap-1.5"
              data-testid={`connect-${provider}-btn`}
            >
              <Plug size={14} weight="bold" />
              {busy ? "Connessione…" : `Connetti ${label.split(" ")[0]}`}
            </Button>
          )}
          {connected && (
            <Button
              onClick={onDisconnect}
              disabled={busy}
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50 gap-1.5"
              data-testid={`disconnect-${provider}-btn`}
            >
              <PlugsConnected size={14} weight="bold" />
              Disconnetti
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
